import React, { useState } from "react";
import { IconButton, Tooltip, TextField, Stack, Typography } from "@mui/material";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import { toast } from "react-toastify";
import AppDialog from "component/ui/AppDialog";
import Service from "features/service/service";

const getSubmitErrorMessage = (error) => {
  const data = error?.response?.data ?? error;
  let apiMessage = "";

  if (typeof data === "string") {
    try {
      apiMessage = JSON.parse(data)?.message || data;
    } catch (e) {
      apiMessage = data;
    }
  } else if (data && typeof data === "object") {
    apiMessage = data.message || "";
  }

  if (String(apiMessage).toLowerCase().includes("to cannot contain null")) {
    return "Unable to notify the IT team because one or more recipients do not have an email address.";
  }
  if (apiMessage && !String(apiMessage).startsWith("Request failed with status code")) {
    return apiMessage;
  }
  return "Unable to submit the IT ticket. Please try again.";
};

// Combined icon + title, guaranteed vertically centered on one line
const DialogTitleWithIcon = () => (
  <Stack direction="row" spacing={1} alignItems="center">
    <HeadsetMicIcon fontSize="medium" />
    <Typography variant="h5" component="span" sx={{ fontWeight: 700, lineHeight: 1 }}>
      Raise IT Ticket
    </Typography>
  </Stack>
);

const ContactSupport = () => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setSubject("");
    setDescription("");
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    const trimmedSubject = subject.trim();
    const trimmedDescription = description.trim();

    if (!trimmedSubject) {
      toast.warn("Please enter a subject.");
      return;
    }
    if (!trimmedDescription) {
      toast.warn("Please enter a description.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await Service.createContactSupportTicket({
        subject: trimmedSubject,
        description: trimmedDescription,
      });
      const ticket = response?.data;
      if (response?.status === 200 && ticket?.id) {
        const ticketRef =  ticket.id || ticket.serviceTicketNumber ;
        toast.success(`IT ticket ${ticketRef} submitted successfully.`, { autoClose: 3000 });
        setOpen(false);
        resetForm();
        return;
      }
      toast.error("Unable to submit the IT ticket. Please try again.");
    } catch (error) {
      toast.error(getSubmitErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Contact Support" arrow>
        <IconButton onClick={handleOpen} aria-label="Contact Support">
          <HeadsetMicIcon sx={{ color: "white" }} />
        </IconButton>
      </Tooltip>
      <AppDialog
        open={open}
        onClose={handleClose}
        title={<DialogTitleWithIcon />}
        primaryLabel="Submit"
        hideSecondary
        onPrimary={handleSubmit}
        primaryDisabled={!subject.trim() || !description.trim()}
        primaryLoading={submitting}
        maxWidth="sm"
      >
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={submitting}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
            inputProps={{ maxLength: 4000 }}
          />
        </Stack>
      </AppDialog>
    </>
  );
};

export default ContactSupport;